import { useCallback, useMemo, useState, type ReactNode } from "react";
import { StyleSheet, View } from "react-native";
import { useTranslation } from "react-i18next";
import {
  countByDay,
  formatLocalYmd,
  toggleOrdered,
  workoutToPlanSeed,
  type Exercise,
  type Plan,
  type PlanSeed,
  type ProgramModel,
  type Session,
  type UnitsPreference,
  type Workout,
} from "@heraion/shared";

import { SaveSessionAsModelDialog } from "../Library/SaveAsModelDialog";
import { usePlans } from "../Plans/usePlans";
import type { ProgramApiFetch } from "../ProgramEditor/types";
import { MonthCalendar } from "../primitives/MonthCalendar";
import { SelectToggle } from "../primitives/SelectToggle";
import type { ConfirmFn, DurableWriteFn } from "../seams";
import type { UiTheme } from "../theme";
import { DiaryModeSwitch } from "./DiaryModeSwitch";
import { PlannedDayList } from "./PlannedDayList";
import { DiarySelectionBar } from "./DiarySelectionBar";
import { SelectionOrderList } from "./SelectionOrderList";

/** The model name a TextField takes (ModelSaveDialog). */
const MODEL_NAME_MAX = 80;

export type DiaryMode = "list" | "calendar";

export type WorkoutsDiaryOptions = {
  theme: UiTheme;
  /** The loaded sessions: the picked ones' names and dates come from here. */
  items: Session[] | null;
  /** The wide web: a Select button beside List | Calendar. */
  showSelectButton: boolean;
  /** The phone: the bar sits at the bottom, in place of the tab bar. */
  dockedBar: boolean;
  apiFetch: ProgramApiFetch;
  durableWrite: DurableWriteFn;
  confirm: ConfirmFn;
  /** One session with its sets, read the host's way. */
  readWorkout: (id: string) => Promise<Workout>;
  /** Resolves, with the ids that are gone, once the list no longer has them. */
  deleteSessions: (ids: readonly string[]) => Promise<readonly string[]>;
  /** Opens the plan editor on a new plan made from one session. */
  onPlanSeed: (seed: PlanSeed) => void;
  /** Opens the program wizard on the model «Create program» saved. */
  onPlanFromModel: (model: Omit<ProgramModel, "id">) => void;
  onOpenPlan: (plan: Plan) => void;
  onStartPlan?: (plan: Plan) => void;
  startingPlanId?: string | null;
  /** The unit the loads are read in, for the plan and the model. null while
   *  the profile is not read: Plan and Create program wait for it. */
  unit: UnitsPreference | null;
  catalog: Exercise[] | null;
  resolveName: (slug: string, snapshot?: string) => string;
  /** A read that failed, said in words. */
  onError: (message: string) => void;
};

// #368: the diary's two modes and its selection, for both hosts. The host
// places the pieces (the phone stacks them, the wide web splits list and
// detail panes) and keeps its own reads, delete and navigation.
// A hold starts the selection; each tap adds or removes, and the number is
// the order of the taps. One picked session can be planned on a day, two or
// more become the days of a program.
export function useWorkoutsDiary(o: WorkoutsDiaryOptions) {
  const { t } = useTranslation();
  const { theme } = o;
  const [mode, setMode] = useState<DiaryMode>("list");
  const [selecting, setSelecting] = useState(false);
  const [ids, setIds] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [model, setModel] = useState<{ workouts: Workout[]; sessions: Session[] } | null>(null);
  const [day, setDay] = useState(() => formatLocalYmd(new Date()));

  const end = useCallback(() => {
    setSelecting(false);
    setIds([]);
  }, []);
  const start = useCallback((id: string) => {
    setSelecting(true);
    setIds([id]);
  }, []);
  const toggle = useCallback((id: string) => setIds((cur) => toggleOrdered(cur, id)), []);

  const picked = useMemo(() => {
    const byId = new Map((o.items ?? []).map((s) => [s.id, s]));
    return ids.flatMap((id) => byId.get(id) ?? []);
  }, [ids, o.items]);
  const nameOf = (s: Session) => s.name ?? t("mobile.workouts.fallbackName");

  const { plans, status } = usePlans({
    apiFetch: o.apiFetch,
    durableWrite: o.durableWrite,
    enabled: mode === "calendar",
  });
  const markers = useMemo(() => countByDay(plans.map((p) => p.scheduledFor)), [plans]);
  const dayPlans = useMemo(() => plans.filter((p) => p.scheduledFor === day), [plans, day]);

  const failed = (e: unknown) => {
    console.warn("[workouts] selection_read", e instanceof Error ? e.message : e);
    o.onError(t("mobile.workouts.loadError"));
  };
  const onPlan = async () => {
    const id = ids[0];
    const unit = o.unit;
    if (!id || !unit) return;
    setBusy(true);
    try {
      const workout = await o.readWorkout(id);
      end();
      o.onPlanSeed(workoutToPlanSeed(workout, unit));
    } catch (e) {
      failed(e);
    } finally {
      setBusy(false);
    }
  };
  const onCreateProgram = async () => {
    setBusy(true);
    try {
      const workouts = await Promise.all(picked.map((s) => o.readWorkout(s.id)));
      const empty = workouts.findIndex((w) => w.entries.length === 0);
      if (empty >= 0) {
        const session = picked[empty];
        o.onError(
          t("mobile.workouts.pickedEmpty", { name: session ? nameOf(session) : "" }),
        );
        return;
      }
      setModel({ workouts, sessions: picked });
    } catch (e) {
      failed(e);
    } finally {
      setBusy(false);
    }
  };
  const onDelete = async () => {
    const yes = await o.confirm({
      title: t("workout.deleteManyConfirm", { count: picked.length }),
      body: picked.map(nameOf).join(", "),
      confirmLabel: t("workout.delete"),
      cancelLabel: t("program.cancel"),
    });
    if (!yes) return;
    setDeleting(true);
    try {
      const gone = new Set(await o.deleteSessions(picked.map((s) => s.id)));
      if (gone.size === picked.length) {
        end();
        return;
      }
      // Some were refused: they stay picked, in their order, and it is said
      // once, here (the host's delete does not also raise its own banner).
      setIds((cur) => cur.filter((id) => !gone.has(id)));
      o.onError(
        gone.size === 0
          ? t("mobile.workouts.deletedNone", { count: picked.length })
          : t("mobile.workouts.deletedSome", { done: gone.size, count: picked.length }),
      );
    } finally {
      setDeleting(false);
    }
  };

  const styles = makeStyles(theme);
  const header: ReactNode = (
    <View style={styles.header}>
      <DiaryModeSwitch theme={theme} mode={mode} disabled={selecting} onChange={setMode} />
      {o.showSelectButton && mode === "list" ? (
        <SelectToggle
          theme={theme}
          active={selecting}
          onPress={() => (selecting ? end() : setSelecting(true))}
          a11yIdle={{ key: "mobile.workouts.selectA11y", defaultValue: "Select workouts" }}
          a11yDone={{ key: "mobile.workouts.selectDoneA11y", defaultValue: "Done selecting workouts" }}
        />
      ) : null}
    </View>
  );

  const bar: ReactNode = selecting ? (
    <DiarySelectionBar
      theme={theme}
      count={picked.length}
      busy={busy}
      ready={o.unit !== null}
      deleting={deleting}
      docked={o.dockedBar}
      showDone={!o.showSelectButton}
      onDone={end}
      onDelete={() => void onDelete()}
      onPlan={() => void onPlan()}
      onCreateProgram={() => void onCreateProgram()}
    />
  ) : null;

  const calendar: ReactNode = (
    <View style={styles.calendar}>
      <MonthCalendar theme={theme} value={day} onChange={setDay} markers={markers} />
    </View>
  );
  const dayList: ReactNode = (
    <PlannedDayList
      theme={theme}
      day={day}
      plans={dayPlans}
      status={status}
      onOpenPlan={o.onOpenPlan}
      onStartPlan={o.onStartPlan}
      startingPlanId={o.startingPlanId}
    />
  );

  const selectionDetail: ReactNode = selecting ? (
    <View style={styles.detail}>
      <SelectionOrderList
        theme={theme}
        sessions={picked}
        title={t("mobile.workouts.pickedTitle")}
        note={
          picked.length === 1
            ? t("mobile.workouts.pickedNoteOne")
            : picked.length > 1
              ? t("mobile.workouts.pickedNoteMany")
              : undefined
        }
      />
    </View>
  ) : null;

  const names = model?.sessions.map(nameOf) ?? [];
  const dialog: ReactNode = model && o.unit ? (
    <SaveSessionAsModelDialog
      theme={theme}
      workouts={model.workouts}
      names={names}
      defaultName={names.join(" · ").slice(0, MODEL_NAME_MAX)}
      catalog={o.catalog}
      unit={o.unit}
      apiFetch={o.apiFetch}
      durableWrite={o.durableWrite}
      resolveName={o.resolveName}
      createProgram
      intro={
        <SelectionOrderList
          theme={theme}
          sessions={model.sessions}
          title={t("mobile.workouts.pickedDays", { count: model.sessions.length })}
          note={t("mobile.workouts.pickedWrongOrder")}
        />
      }
      onPlanFromModel={(m) => {
        end();
        o.onPlanFromModel(m);
      }}
      onClose={() => setModel(null)}
    />
  ) : null;

  return {
    mode,
    selecting,
    endSelection: end,
    /** Spread on WorkoutsView. */
    listProps: {
      selection: selecting ? ids : null,
      onToggleSelect: toggle,
      onStartSelect: start,
    },
    header,
    bar,
    calendar,
    dayList,
    selectionDetail,
    dialog,
  };
}

function makeStyles(theme: UiTheme) {
  const { palette: p, spacing: s } = theme;
  return StyleSheet.create({
    header: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
      gap: s.s3,
      paddingHorizontal: s.s4,
      paddingVertical: s.s2,
      borderBottomWidth: StyleSheet.hairlineWidth,
      borderBottomColor: p.border,
    },
    calendar: { padding: s.s3 },
    detail: { padding: s.s4 },
  });
}

export type WorkoutsDiary = ReturnType<typeof useWorkoutsDiary>;
